import type {
  File as DocxFile,
  IPageMarginAttributes,
  IPageSizeAttributes,
  ISectionOptions,
  Paragraph,
  Table,
} from 'docx';
import { Document as DocxDocument, PageOrientation } from 'docx';
import type { Editor, NodeEntry } from 'slate';

import {
  createDocxAlineContentBlock,
  createDocxComments,
  createDocxFooters,
  createDocxHeaders,
  createDocxStyles,
  createNumberingOptionsConfigs,
  DocumentContext,
} from './internal';
import type { DocParts, DocumentElement, FooterElement, HeaderElement } from '../../../slate-api';
import { AlineTransforms, defaultPageDimensions, Queries } from '../../../slate-api';
import type { ImageMap, UUID } from '../../../types';
import type { WithContext } from '../../../util';
import { inToTwips } from '../../../util';

export type DBComment = {
  authorId: string | null;
  createdAt: string;
  id: string;
  importedAuthorName: string | null;
  // null used for comments that are replies.
  nodeRefId: string | null;
  parentCommentId: string | null;
  resolved: boolean;
  text: string;
};
export type ParentDBComment = DBComment & { nodeRefId: string };

type IPropertiesOptions = ConstructorParameters<typeof DocxFile>[0];

export function createDocxDocument(
  docParts: DocParts,
  options: {
    authorNames: Map<string, string>;
    dbComments: DBComment[];
    imageMap: ImageMap;
    alineDocumentId?: UUID;
    docProperties?: Pick<IPropertiesOptions, 'title'>;
    /**
     * Writes a hidden `\uuid\` run into every signature field and collects the anchors on
     * the returned context. DocuSign needs those anchors to place its own signature tabs,
     * so only the envelope build opts in. Every other export leaves them out.
     */
    includeDocuSignUuidTags?: boolean;
  }
): WithContext<DocxDocument, DocumentContext> {
  const {
    alineDocumentId,
    authorNames,
    dbComments,
    docProperties,
    imageMap,
    includeDocuSignUuidTags = false,
  } = options;
  const editor = AlineTransforms.createQueryEditorFromJson(docParts);
  const documentEntry = Queries.getDocument(editor);
  const [document] = documentEntry;
  const styles = createDocxStyles(document);
  const { commentMap, docXComments } = createDocxComments(editor, { authorNames, dbComments });
  const documentContext = new DocumentContext({
    authorNames,
    commentMap,
    imageMap,
    includeDocuSignUuidTags,
    listDefinitions: document.listDefinitions ?? {},
  });
  const docChildren = createDocxChildren(documentEntry, { context: documentContext });
  const headers = createDocxHeaders(editor, { context: documentContext });
  const footers = createDocxFooters(editor, { context: documentContext });

  const doc = new DocxDocument({
    ...docProperties,
    ...styles,
    comments: docXComments,
    ...(alineDocumentId
      ? {
          customProperties: [{ name: 'alineDocumentId', value: alineDocumentId }],
        }
      : {}),
    defaultTabStop: inToTwips(Queries.activeDefaultTabStopIn(document)),
    evenAndOddHeaderAndFooters: document.headerFooter.evenOdd,
    numbering: {
      config: createNumberingOptionsConfigs(documentContext),
    },
    sections: [
      {
        children: docChildren,
        footers,
        headers,
        properties: createDocxSectionProps(editor),
      },
    ],
  });

  return { context: documentContext, value: doc };
}

/**
 * Utility function for creating different attributes/properties for the DOCX document.
 */
function createDocxSectionProps(editor: Editor): ISectionOptions['properties'] {
  const [document] = Queries.getDocument(editor);
  const pageDimensions = document.pageDimensions ?? defaultPageDimensions;
  const { height, marginBottom, marginLeft, marginRight, marginTop, width } = pageDimensions;
  const orientation = height >= width ? PageOrientation.PORTRAIT : PageOrientation.LANDSCAPE;
  const footersPartEntry = Queries.getFootersElement(editor);
  const headersPartEntry = Queries.getHeadersElement(editor);
  const [footersPart] = footersPartEntry ?? [];
  const [headersPart] = headersPartEntry ?? [];
  const activeFooterMargin = footersPart?.footerMargin ?? 0.5;
  const activeHeaderMargin = headersPart?.headerMargin ?? 0.5;
  const pageNumbering = Queries.getActivePageNumbering(document);
  const pageSizeAttributes: IPageSizeAttributes = {
    height: inToTwips(height),
    orientation,
    width: inToTwips(width),
  };
  const marginAttributes: IPageMarginAttributes = {
    bottom: inToTwips(marginBottom),
    footer: inToTwips(activeFooterMargin),
    header: inToTwips(activeHeaderMargin),
    left: inToTwips(marginLeft),
    right: inToTwips(marginRight),
    top: inToTwips(marginTop),
  };

  return {
    page: {
      margin: marginAttributes,
      pageNumbers: {
        formatType: pageNumbering.format,
        start: pageNumbering.start,
      },
      size: pageSizeAttributes,
    },
    /**
     * Per the OOXML spec (http://officeopenxml.com/WPSectionFooterReference.php), "first - the footer should be applied to the first page of the section.
     * The actual appearance is dependent upon the setting of the titlePg element in sectPr."
     */
    titlePage: document.headerFooter.diffFirstPage,
  };
}

export function createDocxChildren(
  cMNodeEntry: NodeEntry<DocumentElement | FooterElement | HeaderElement>,
  options: { context: DocumentContext }
): (Paragraph | Table)[] {
  const [cMNode, cMPath] = cMNodeEntry;
  return cMNode.children.flatMap((child, index) =>
    createDocxAlineContentBlock([child, cMPath.concat(index)], options)
  );
}
